import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';

import { expValEquals } from '@confluence/feature-experiments';

// A delivery acknowledgement is not a persisted CAP session. Bound the optimistic
// indicator even when downstream failures cannot be observed by the comment surface.
export const COMMENT_AGENT_STARTUP_TIMEOUT_MS = 30_000;
export type CommentAgentIdentity = {
	agentId?: string | null;
	agentName?: string | null;
	avatarUrl?: string | null;
};

type PendingSessionScope = {
	contentId: string;
	userId: string;
};

type PendingSession = {
	scope?: PendingSessionScope;
	timer: ReturnType<typeof setTimeout>;
	agentIdentity?: CommentAgentIdentity;
	startedAt: number;
	isDefaultAgent?: boolean;
	createAnalyticsEvent?: CreateUIAnalyticsEvent;
};
const pending = new Map<string, PendingSession>();
const listeners = new Map<string, Set<() => void>>();
const scopeListeners = new Map<string, Set<() => void>>();

function getScopeKey({ contentId, userId }: PendingSessionScope): string {
	return JSON.stringify([contentId, userId]);
}

function notifyScope(scope: PendingSessionScope | undefined): void {
	if (scope) {
		scopeListeners.get(getScopeKey(scope))?.forEach((listener) => listener());
	}
}

export function clearOptimisticCommentAgentSession(
	commentId: string,
	outcome?: 'sessionFound' | 'timedOut',
): void {
	const session = pending.get(commentId);
	if (!session) {
		return;
	}
	clearTimeout(session.timer);
	pending.delete(commentId);
	notifyScope(session.scope);
	listeners.get(commentId)?.forEach((listener) => listener());
	if (outcome) {
		try {
			session
				.createAnalyticsEvent?.({
					type: 'sendOperationalEvent',
					data: {
						action: outcome,
						actionSubject: 'commentAgentSessionDiscovery',
						source: 'agentMention',
						attributes: { elapsedTimeMs: Date.now() - session.startedAt },
					},
				})
				?.fire();
		} catch {
			// Best-effort reporting must not throw from the discovery timeout callback.
		}
	}
}

export function startOptimisticCommentAgentSession(
	commentId: string,
	agentIdentity?: CommentAgentIdentity,
	createAnalyticsEvent?: CreateUIAnalyticsEvent,
	scope?: PendingSessionScope,
): void {
	if (!commentId || pending.has(commentId)) {
		return;
	}
	pending.set(commentId, {
		scope,
		agentIdentity,
		startedAt: Date.now(),
		createAnalyticsEvent,
		timer: setTimeout(
			() => clearOptimisticCommentAgentSession(commentId, 'timedOut'),
			COMMENT_AGENT_STARTUP_TIMEOUT_MS,
		),
	});
	notifyScope(scope);
	listeners.get(commentId)?.forEach((listener) => listener());
}

export function getOptimisticCommentAgentIdentity(
	commentId: string,
): CommentAgentIdentity | undefined {
	return pending.get(commentId)?.agentIdentity;
}

export function getOptimisticCommentAgentSessionMetrics(
	commentId: string,
): Pick<PendingSession, 'startedAt' | 'isDefaultAgent'> | undefined {
	return pending.get(commentId);
}

export function setOptimisticCommentAgentSessionIsDefault(
	commentId: string,
	isDefaultAgent: boolean,
): void {
	const session = pending.get(commentId);
	if (session) session.isDefaultAgent = isDefaultAgent;
}

export function clearOptimisticCommentAgentSessionsForTesting(): void {
	pending.forEach(({ timer }) => clearTimeout(timer));
	pending.clear();
	listeners.clear();
	scopeListeners.clear();
}

function subscribeToComment(commentId: string, listener: () => void): () => void {
	if (!commentId) {
		return () => {};
	}
	const subscribers = listeners.get(commentId) ?? new Set<() => void>();
	listeners.set(commentId, subscribers);
	subscribers.add(listener);
	return () => {
		subscribers.delete(listener);
		if (subscribers.size === 0) {
			listeners.delete(commentId);
			// StrictMode re-subscribes synchronously during effect replay.
			queueMicrotask(() => {
				if (!listeners.has(commentId)) {
					clearOptimisticCommentAgentSession(commentId);
				}
			});
		}
	};
}

export function useOptimisticCommentAgentSession(commentId: string, hasSession: boolean): boolean {
	const subscribe = useCallback(
		(listener: () => void) => subscribeToComment(commentId, listener),
		[commentId],
	);
	const getSnapshot = useCallback(() => pending.has(commentId), [commentId]);
	const isPending = useSyncExternalStore(subscribe, getSnapshot, () => false);

	useEffect(() => {
		if (commentId && hasSession) {
			clearOptimisticCommentAgentSession(commentId, 'sessionFound');
		}
	}, [commentId, hasSession]);

	return !hasSession && isPending;
}

export function useOptimisticCommentAgentSessions(
	commentIds: readonly string[],
): ReadonlySet<string> {
	// Stable by contents: callers may derive a new ID array from a reply list each render.
	const idsKey = expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true)
		? JSON.stringify([...new Set(commentIds.filter(Boolean))].sort())
		: '[]';
	const ids: string[] = useMemo(() => JSON.parse(idsKey), [idsKey]);
	const subscribe = useCallback(
		(listener: () => void) => {
			const unsubscribe = ids.map((id) => subscribeToComment(id, listener));
			return () => unsubscribe.forEach((dispose) => dispose());
		},
		[ids],
	);
	const getSnapshot = useCallback(() => JSON.stringify(ids.filter((id) => pending.has(id))), [ids]);
	const snapshot = useSyncExternalStore(subscribe, getSnapshot, () => '[]');
	return useMemo(() => new Set<string>(JSON.parse(snapshot)), [snapshot]);
}

export function usePendingCommentAgentSessionIds(
	contentId: string,
	userId: string,
): readonly string[] {
	const scopeKey = getScopeKey({ contentId, userId });
	const subscribe = useCallback(
		(listener: () => void) => {
			const subscribers = scopeListeners.get(scopeKey) ?? new Set<() => void>();
			scopeListeners.set(scopeKey, subscribers);
			subscribers.add(listener);
			return () => {
				subscribers.delete(listener);
				if (subscribers.size === 0) {
					scopeListeners.delete(scopeKey);
				}
			};
		},
		[scopeKey],
	);
	const getSnapshot = useCallback(() => {
		const commentIds: string[] = [];
		if (contentId && userId) {
			for (const [commentId, session] of pending) {
				if (session.scope?.contentId === contentId && session.scope.userId === userId) {
					commentIds.push(commentId);
				}
			}
		}
		return JSON.stringify(commentIds);
	}, [contentId, userId]);
	const snapshot = useSyncExternalStore(subscribe, getSnapshot, () => '[]');
	return useMemo(() => JSON.parse(snapshot), [snapshot]);
}
